/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../../config/enum';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { ApiRequest } from '../../../interface/common.interface';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ocpiApi from '../../../utils/api/ocpi';

/**
 * Dispatches a notification message using the shared notification slice's action type.
 * Avoids a circular import on the notification slice from within this one.
 * @param {any} dispatch - The thunk dispatch function.
 * @param {any} message - The message to show.
 * @param {any} type - The notification type.
 */
const notify = (dispatch: any, message: string, type: 'success' | 'error') => {
  dispatch({
    type: 'notification/setNotificationMessage',
    payload: { status: true, message, type },
  });
};

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

interface CpoLocationsRequest extends ApiRequest {
  countryCode: string;
  partyId: string;
}

interface FetchCpoLocationsResponse {
  results: any[];
  limit: number;
  page: number;
}

/**
 * Fetches a page of OCPI locations for a CPO role from the internal gateway.
 */
export const fetchCpoLocations = createAsyncThunk<FetchCpoLocationsResponse, CpoLocationsRequest>(
  'cpo-locations/fetchCpoLocations',
  async ({ countryCode, partyId, page, limit }, { dispatch, rejectWithValue }) => {
    try {
      const resolvedPage = page || DefaultApiParameters.page;
      const resolvedLimit = limit || DefaultApiParameters.limit;
      const response = await ocpiApi.getInternalCpoLocations(
        countryCode,
        partyId,
        resolvedPage,
        resolvedLimit,
      );
      const results = response?.data?.data ?? response?.data ?? [];
      return {
        results: Array.isArray(results) ? results : [],
        limit: resolvedLimit,
        page: resolvedPage,
      };
    } catch (error: any) {
      notify(
        dispatch,
        error?.response?.data?.message || 'Failed to fetch OCPI locations.',
        'error',
      );
      return rejectWithValue(error?.response?.data);
    }
  },
);

/**
 * Synchronizes CPO locations from the CPO backend, falling back to a plain fetch when the
 * sync response doesn't return the refreshed list directly.
 */
export const syncCpoLocations = createAsyncThunk<
  { results: any[] } | undefined,
  { countryCode: string; partyId: string }
>(
  'cpo-locations/syncCpoLocations',
  async ({ countryCode, partyId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await ocpiApi.syncInternalCpoLocations(countryCode, partyId);
      const results = response?.data?.data ?? response?.data;
      if (Array.isArray(results)) {
        return { results };
      }
      await dispatch(fetchCpoLocations({ countryCode, partyId }));
      return undefined;
    } catch (error: any) {
      // A 404 means the CPO simply has no locations to sync yet, not a real failure.
      if (error?.response?.status !== 404) {
        notify(dispatch, error?.message || 'Failed to sync locations.', 'error');
      }
      await dispatch(fetchCpoLocations({ countryCode, partyId }));
      return rejectWithValue(error?.response?.data);
    }
  },
);

/**
 * Broadcasts all CPO locations to connected partners.
 */
export const broadcastCpoLocations = createAsyncThunk<
  void,
  { countryCode: string; partyId: string }
>('cpo-locations/broadcastCpoLocations', async ({ countryCode, partyId }, { dispatch }) => {
  try {
    const response = await ocpiApi.broadcastInternalCpoLocations(countryCode, partyId);
    notify(
      dispatch,
      response?.data?.status_message || response?.status_message || 'Broadcast triggered.',
      'success',
    );
  } catch (error: any) {
    notify(dispatch, error?.message || 'Failed to broadcast.', 'error');
  }
});

/**
 * Manually pushes a specific location (with selected EVSEs) to a partner connection.
 */
export const pushCpoLocation = createAsyncThunk<
  void,
  {
    countryCode: string;
    partyId: string;
    locationId: string;
    connectionId: string;
    evseUids: string[];
  }
>(
  'cpo-locations/pushCpoLocation',
  async (
    { countryCode, partyId, locationId, connectionId, evseUids },
    { dispatch, rejectWithValue },
  ) => {
    try {
      await ocpiApi.pushLocation(countryCode, partyId, locationId, { connectionId, evseUids });
      notify(dispatch, 'Location pushed to partner successfully.', 'success');
      return undefined;
    } catch (error: any) {
      notify(dispatch, error?.message || 'Failed to push location.', 'error');
      return rejectWithValue(error?.response?.data);
    }
  },
);

/**
 * Resolves the filter list for the CPO locations table. Filter options are known upfront (there
 * is no backend filter-list endpoint for OCPI locations), so this only exists to satisfy
 * CustomTable's filterListToDispatch contract.
 */
export const fetchCpoLocationFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'cpo-locations/fetchCpoLocationFilters',
  async () => availableFilters,
);

interface CPOLocationsInitialState {
  locations: any[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  sortBy: string;
  sortField: string;
}

const initialState: CPOLocationsInitialState = {
  locations: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const cpoLocationSlice = createSlice({
  name: 'cpo-locations',
  initialState,
  reducers: {
    /**
     * Update CPO Locations filters
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing filter updates.
     */
    updateCPOLocationsFilters: (state: any, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current slice state.
     */
    disableAccountManagementFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Extra reducers to handle thunk requests.
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchCpoLocations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCpoLocations.fulfilled, (state, action) => {
        state.loading = false;
        state.locations = action.payload.results;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalResults = action.payload.results.length;
        state.totalPages = Math.max(
          1,
          Math.ceil(action.payload.results.length / action.payload.limit),
        );
      })
      .addCase(fetchCpoLocations.rejected, (state) => {
        state.loading = false;
      })
      .addCase(syncCpoLocations.pending, (state) => {
        state.loading = true;
      })
      .addCase(syncCpoLocations.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.results) {
          state.locations = action.payload.results;
          state.totalResults = action.payload.results.length;
          state.totalPages = Math.max(1, Math.ceil(action.payload.results.length / state.limit));
        }
      })
      .addCase(syncCpoLocations.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchCpoLocationFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(fetchCpoLocationFilters.fulfilled, (state, action) => {
        state.filterListLoading = false;
        state.filters = action.payload;
      })
      .addCase(fetchCpoLocationFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const { updateCPOLocationsFilters, disableAccountManagementFilterApplied, setSortData } =
  cpoLocationSlice.actions;

export default cpoLocationSlice.reducer;
